import { Component, inject, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { PlannedEventApi } from "@ramudden/data-access/resource/planned-event-api";
import { IPlannedEvent } from "@ramudden/models/planned-event";
import { FilterDescriptor, FilterOperator, SearchParameters, ServiceRequestOptions } from "@ramudden/models/search";
import { SvgComponent } from "@ramudden/ui";
import moment from "moment/moment";
import { firstValueFrom } from "rxjs";
import { DefaultComponent } from "../../layout/content/default/default.component";
import { AppService } from "../../services/app.service";
import { TitleService } from "../../services/title.service";

@Component({
    selector: "app-team-overview",
    standalone: true,
    imports: [DefaultComponent, TranslateModule, SvgComponent],
    templateUrl: "./team-overview.component.html",
    styleUrl: "./team-overview.component.scss",
})
export class TeamOverviewComponent implements OnInit {
    private readonly plannedEventApi = inject(PlannedEventApi);
    private readonly appService = inject(AppService);
    private readonly router = inject(Router);
    private readonly titleService = inject(TitleService);

    teams: IPlannedEvent[] = [];
    isLoading = true;

    async ngOnInit() {
        this.titleService.title = "Coordinator View";
        await this.loadTeams();
    }

    async loadTeams() {
        this.isLoading = true;
        try {
            const noon = moment().hour(12).minute(0).second(0);
            const searchParameters = new SearchParameters();
            searchParameters.filter = [];

            if (moment() > noon) {
                searchParameters.filter.push(
                    new FilterDescriptor("EventDate", moment().format("YYYY/MM/DD"), FilterOperator.equals),
                );
            } else {
                searchParameters.filter.push(
                    new FilterDescriptor(
                        "EventDate",
                        moment().subtract(1, "days").format("YYYY/MM/DD"),
                        FilterOperator.greaterThanOrEqualTo,
                    ),
                );
                searchParameters.filter.push(
                    new FilterDescriptor(
                        "EventDate",
                        moment().format("YYYY/MM/DD"),
                        FilterOperator.lessThanOrEqualTo,
                    ),
                );
            }

            const serviceRequestOptions = new ServiceRequestOptions();
            serviceRequestOptions.includes.add("PlannedEvent", "PlannedWorkers");
            serviceRequestOptions.includes.add("PlannedEvent", "PlannedAssignments");
            serviceRequestOptions.includes.add("PlannedWorker", "Worker");

            this.teams = await firstValueFrom(
                this.plannedEventApi.getAll$(searchParameters, false, serviceRequestOptions),
            );
        } catch (error) {
            console.error("Failed to load teams", error);
            this.teams = [];
        } finally {
            this.isLoading = false;
        }
    }

    selectTeam(team: IPlannedEvent) {
        this.appService.viewingPlannedEvent = team;
        this.appService.saveState();
        this.router.navigate(["/assignments"]).then(() => {
            location.reload();
        });
    }

    getTeamLeader(team: IPlannedEvent): string {
        if (!team?.plannedWorkers?.length) return "—";
        const leader = team.plannedWorkers[0];
        return `${leader.worker.firstName} ${leader.worker.lastName}`;
    }

    getTeamMembers(team: IPlannedEvent): string {
        if (!team?.plannedWorkers?.length) return "";
        return team.plannedWorkers.map((pw) => `${pw.worker.firstName} ${pw.worker.lastName}`).join(", ");
    }

    getAssignmentCount(team: IPlannedEvent): number {
        return team?.plannedAssignments?.length ?? 0;
    }
}
